/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */

import React from 'react';
import AsyncSelect from 'react-select/async';
import set from 'lodash/set';
import get from 'lodash/get';
import { MenuListComponentProps } from 'react-select/src/components/Menu';
import config from 'jss-boilerplate/temp/config.js';

import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';
import { isJssField } from 'core/utils/sitecore-utils';
import IProps from 'pods/forms/components/formFieldProps.d';
import { ADDRESS, MANUAL_ADDRESS } from 'core/constants/form';

import Richtext from 'core/components/RichText';
import { AddressDataType } from 'core/api/client/SystemValidatorApi';
import api from 'core/hooks/useGetAddressFields/api';
import * as S from './AddressAutocomplete.style';

interface IAddressOption {
	label: string;
	value: string;
	full_address?: string;
	address?: AddressDataType;
	customLabel?: JSX.Element;
}

const myOptionLabel = ({ label, customLabel }: IAddressOption) => {
	if (!!customLabel) {
		return customLabel;
	} else {
		return <>{label}</>;
	}
};

// API base config
const apiConfig = config;

const MenuList = (props: MenuListComponentProps<any>) => {
	return <S.MenuList {...props}>{props.children}</S.MenuList>;
};

const AddressAutocomplete = ({
	config,
	computed,
	htmlId,
	value,
	handleChange,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
	path,
	setValues,
	formValues,
}: IProps) => {
	const { data = {}, appendMsg, bottomMsg } = config;
	const {
		placeholder,
		labelHelpText,
		inputAttrs = {},
		fullWidth,
		emptyResultLabel,
		noMatchLabel,
	} = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = isJssField(labelHelpText)
		? labelHelpText
		: renderTextNode(labelHelpText, 'span');
	const tooltip = renderTextNode(data.tooltip);
	const errorMsg = getDisplayError();
	const hasError = !!errorMsg;
	const { required, disabled } = computed;

	const [input, setInput] = React.useState('');

	const systemValidationBase = `${apiConfig.australianSuperApi?.apiDomain}${apiConfig.australianSuperApi?.apiPathSystemValidation}`;

	const { data: response, loading } = api.useGetAddress({
		base: systemValidationBase,
		queryParams: { full_address: input },
	});

	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		errorMsg,
		required,
		appendMsg,
		value,
		disabled,
		bottomMsg,
		placeholder,
		type: inputAttrs?.type,
		fullWidth: data.fullWidth,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	const addressPathPrefix = path.split('.')[0];
	const addressValue = get(
		formValues,
		`${addressPathPrefix}.${ADDRESS.ADDRESS}`,
	);

	const onInputChange = (val: string) => {
		setInput(val);
	};

	// TODO: test with jss components
	//const debounced = React.useCallback(debounce(refetch, 200), []);

	React.useEffect(() => {
		set(
			formValues,
			`${addressPathPrefix}.${ADDRESS.ADDRESS_LOOKUP}`,
			addressValue,
		);
	}, [addressValue]);

	const onChange = (val: IAddressOption) => {
		if (val?.value !== MANUAL_ADDRESS) {
			const addressPathPrefix = path.split('.')[0];

			const {
				subdwelling_name = '',
				street_number = '',
				street_name = '',
				street_type = '',
				suburb = '', // TODO: missing from api
				state = '',
				postcode = '',
				postal = '',
			} = val?.address ?? {};

			const getStreet1 = (): string => {
				let street1 = '';
				if (subdwelling_name.length > 0) {
					street1 += `${subdwelling_name} `;
				}
				if (street_number.length > 0) {
					street1 += `${street_number} `;
				}
				if (street_name.length > 0) {
					street1 += `${street_name} `;
				}
				if (street_type.length > 0) {
					street1 += street_type;
				}

				return street1;
			};
			//this will set address_line_1 to postal address if all street fields are empty
			//this scenario is expected when the given address is postal
			set(
				formValues,
				`${addressPathPrefix}.${ADDRESS.STREET_1}`,
				getStreet1() || postal,
			);
			set(formValues, `${addressPathPrefix}.${ADDRESS.STREET_2}`, '');
			// todo: remove N/A once API is fixed
			set(
				formValues,
				`${addressPathPrefix}.${ADDRESS.SUBURB}`,
				suburb ?? 'N/A',
			);
			set(formValues, `${addressPathPrefix}.${ADDRESS.STATE}`, state);
			set(formValues, `${addressPathPrefix}.${ADDRESS.POSTCODE}`, postcode);

			setValues(formValues);
		}

		handleChange(val?.value);
	};

	const _loadAutocompleteOptions = (searchText: string) => {
		return new Promise(resolve =>
			// Adding a delay of 2 seconds in order to delay the API call once the user entered a search string.
			setTimeout(() => {
				{
					// Condition: The user needs to input at least 5 character on the
					// address search field so that the auto-suggest from the API will appear on the UI.
					if (searchText.length >= 5) {
						// API result that matched the query params on address field
						const addressData = response?.data?.addresses ?? [];

						// Mapping the response and make it a selection options
						const addressOptions: IAddressOption[] = addressData
							?.map(item => ({
								label: item.full_address,
								value: item.full_address,
								address: item.address,
							}))
							.slice(0, 5);
						setTimeout(() => resolve(addressOptions), 200);
						const isResultEmpty = addressOptions.length === 0;
						addressOptions.push({
							label: isResultEmpty ? 'empty result' : 'manual',
							value: 'manual',
							customLabel: (
								<Richtext
									content={
										isResultEmpty
											? emptyResultLabel?.value
											: noMatchLabel?.value
									}
								/>
							),
						});
					} else {
						resolve([
							{
								label: 'empty result',
								value: 'manual',
								customLabel: <Richtext content={emptyResultLabel?.value} />,
							},
						]);
					}
				}
			}, 2000),
		);
	};

	return (
		<UpdatedCtrlHolder {..._ctrlHolderProps}>
			<S.AddressAutocompleteContainer {...{ disabled, hasError, fullWidth }}>
				<AsyncSelect
					defaultValue={{
						label: addressValue,
						value: addressValue,
						customLabel: addressValue,
					}}
					placeholder={placeholder}
					className="formBuilder__addressAutocomplete"
					classNamePrefix="formBuilder__addressAutocomplete"
					isDisabled={disabled}
					cacheOptions
					loadOptions={_loadAutocompleteOptions}
					onChange={onChange as any}
					onInputChange={onInputChange}
					isLoading={loading}
					components={{
						MenuList,
						DropdownIndicator: null,
					}}
					formatOptionLabel={myOptionLabel}
					isClearable
					openMenuOnFocus={false}
					openMenuOnClick={false}
					noOptionsMessage={(input: any) =>
						input.inputValue.length >= 5 ? 'No results' : null
					}
					aria-label="Select"
					menuPortalTarget={document.body}
					menuPosition="fixed"
				/>
			</S.AddressAutocompleteContainer>
		</UpdatedCtrlHolder>
	);
};

export default AddressAutocomplete;
